function areWordsSimilar(word1, word2, threshold = 90) {
    // Helper function to calculate Levenshtein distance
    function getLevenshteinDistance(a, b) {
        const dp = Array(a.length + 1).fill(null).map(() => Array(b.length + 1).fill(null));

        for (let i = 0; i <= a.length; i++) dp[i][0] = i;
        for (let j = 0; j <= b.length; j++) dp[0][j] = j;

        for (let i = 1; i <= a.length; i++) {
            for (let j = 1; j <= b.length; j++) {
                const cost = a[i - 1] === b[j - 1] ? 0 : 1;
                dp[i][j] = Math.min(
                    dp[i - 1][j] + 1,       // Deletion
                    dp[i][j - 1] + 1,       // Insertion
                    dp[i - 1][j - 1] + cost // Substitution
                );
            }
        }
        return dp[a.length][b.length];
    }

    // Calculate the similarity percentage
    const distance = getLevenshteinDistance(word1, word2);
    const maxLength = Math.max(word1.length, word2.length);
    const similarity = ((maxLength - distance) / maxLength) * 100;

    // Return true if similarity is above the threshold
    return similarity >= threshold;
}

function generateWordPermutations(input) {
    const result = [];

    // Helper function to generate permutations
    function permute(words, current = []) {
        if (words.length === 0) {
            result.push(current.join(" ")); // Join words to form a reordered string
        } else {
            for (let i = 0; i < words.length; i++) {
                const next = words.slice(0, i).concat(words.slice(i + 1));
                permute(next, current.concat(words[i]));
            }
        }
    }

    const words = input.trim().split(/\s+/); // Split the input string into words
    permute(words);
    return result;
}

class Search extends HTMLElement {
    parent = this.closest('hoculus-search');

    form = this.querySelector('form');
    productsList = this.parent.querySelector('.hoculus-products-list');
    loader = this.parent.querySelector('.hoculus-search-loader');

    issues = new Set();

    keywordsListTitle = this.parent.querySelector('.hoculus-keywords-list-title');
    keywordsList = this.parent.querySelector('.hoculus-keywords-list');

    noResults = this.parent.querySelector('.hoculus-search-no-results');

    csvUrl = this.parent.dataset.csvUrl;
    csvData = [];

    page = 0;
    resPerPage = parseInt(this.parent.dataset.paginationResultsLimit) || 5;
    loadMoreBtnTemplate = this.parent.querySelector('.hoculus-load-more-template');

    sortingType = this.parent.dataset.sortingType;

    constructor() {
        super();
        
        this.form.addEventListener('submit', async evt => {
            evt.preventDefault();

            // Check if csv has already been fetched
            if (!this.csvData.length) await this.getCSV();

            const query = new FormData(evt.target).get('query');

            this.showLoader();
            this.clear();
            this.handleSubmit(evt, query);
        });
    }

    handleSubmit(evt) {}

    showLoader() { this.loader.style.display = 'block'; }

    hideLoader() { this.loader.style.display = 'none'; }

    clear() {
        this.productsList.innerHTML = '';
        this.keywordsList.innerHTML = '';
        this.parent.querySelector('.hoculus-load-more-btn-wrapper')?.remove();

        this.keywordsListTitle.style.display = 'none';
        this.keywordsList.style.display = 'none';

        this.noResults.style.display = 'none';

        this.issues.clear();
        this.keywords?.clear();
        this.page = 0;
    }

    // CSV
    async getCSV() {
        try {
            const response = await fetch(this.csvUrl);
            const data = await response.text();
            this.csvData = [];
            this.csvData = this.parseCSV(data);
        } catch (error) {
            console.log(error);
        }
    }

    parseCSV(csvData) {
        return csvData.split('\n').map(row => {
            const regex = /"(.*?)"|([^,]+)|,/g;
            const result = [];
            let match;

            while ((match = regex.exec(row)) !== null) {
                if (match[1] !== undefined) {
                    result.push(match[1]);
                } else if (match[2] !== undefined) {
                    result.push(match[2]);
                }
            }
            return result;
        });
    }
    // END CSV

    // SEARCH
    handleSearchIssue(query) {
        for (const row of this.csvData) {
            const [issue, page, subject, description, notes] = row;
            const foundIssue = this.search(`${subject ?? ''} ${description ?? ''} ${notes ?? ''}`, query, issue?.split(','));
            if (foundIssue) return issue;
        }
        return false;
    }
    
    /**
     * Handles the search functionality for the given text and query.
     *
     * @param {string} text - The text to search within.
     * @param {string} query - The query to search for.
     */
    search(text, query) {
        return (text && query) ? text.trim().toLowerCase().includes(query.trim().toLowerCase()) : false;
    }
    // END SEARCH

    // RENDER
    async getProductsStringHTML(issue) {
        return new Promise(async (res, rej) => {
            try {
                const response = await fetch(`/search?type=product&q=Back Issue ${issue}`);
                const htmlString = await response.text();
                res(htmlString);
            } catch (error) {
                console.log(error);
                rej(error);
            }
        });
    }

    renderProducts(htmlString, issue) {
        const html = new DOMParser().parseFromString(htmlString, 'text/html');

        // Remove products that don't match the issue
        html.querySelectorAll('[data-back-issue],[data-remove-from-magazine-search]').forEach(productCard => {
             if (productCard.dataset.backIssue !== issue) productCard.remove();
        });

        const productListHTMLString = html.querySelector('.search__products').innerHTML;
        this.productsList.insertAdjacentHTML('beforeend', productListHTMLString);
    }
    // END RENDER

    renderLoadMoreButton() {
        const templateContent = this.loadMoreBtnTemplate.content.cloneNode(true);
        const button = templateContent.querySelector('button');
        button.addEventListener('click', () => {
            this.page++;
            this.showLoader();
            this.fetchHtmlStrings();
            this.parent.querySelector('.hoculus-load-more-btn-wrapper')?.remove();
        });
        this.loadMoreBtnTemplate.after(templateContent);
    }

    fetchHtmlStrings() {
        const issuesArray = this.sortedIssues()

        const paginatedIssues = issuesArray.slice(this.page * this.resPerPage, ((this.page + 1) * this.resPerPage) + 1); // +1 at the end to check if there's a next page

        const hasNextPage = paginatedIssues.length > this.resPerPage;
        if (hasNextPage) paginatedIssues.pop();

        Promise.allSettled(paginatedIssues.map(this.getProductsStringHTML.bind(this)))
            .then((htmlStrings) => htmlStrings.forEach(({ value: htmlString, status }, i) => {
                if (status === 'fulfilled') this.renderProducts(htmlString, issuesArray[i + (this.page * this.resPerPage)]);
            }))
            .finally(() => {
                if (issuesArray.length <= this.resPerPage) this.issues.clear();
                else if (hasNextPage) this.renderLoadMoreButton();
                this.keywords?.clear();
                this.hideLoader();
            });
    }

    sortedIssues() {
        if (this.sortingType === 'asc') return [...this.issues].sort((a, b) => a - b);
        if (this.sortingType === 'desc') return [...this.issues].sort((a, b) => b - a);
        return [...this.issues]
    }
}

class SearchByChassis extends Search {
    constructor() { super(); }

    async handleSubmit(evt, query) {
        const foundIssue = this.handleSearchIssue(query, 'chassis');
        
        if (foundIssue) {
            foundIssue.split(',').forEach(issue => this.issues.add(issue.trim()));
            this.fetchHtmlStrings();
        } else {
            this.noResults.style.display = 'block';
            this.hideLoader();
        }
    }

    search(text, query) {
        return (text && query) ? text.trim().toLowerCase().match(/\bs\/n \d+\b/gi)?.find(chassis => chassis === (query.trim().toLowerCase().includes('s/n') ? "" : "s/n ") + query.trim().toLowerCase()) : false;
    }
}

class SearchByText extends Search {
    keywords = new Set();
    keywordsLimit = this.parent.dataset.keywordsToShow;
    keywordsThreshold = this.parent.dataset.similarWordsThreshold;

    constructor() { super(); }

    connectedCallback() {
        this.keywordsList.addEventListener('click', evt => {
            if (!evt.target.value) return;
            this.form.querySelector('input[name="query"]').value = evt.target.value;
            this.form.querySelector('button').click();
        });
    }

    handleSubmit(evt, query) {
        this.handleSearchIssue(query);

        if (this.issues.size === 0 && this.keywords.size) {
            this.renderKeywords();
            this.keywords.clear();
            this.hideLoader();
        }

        if (this.issues.size) {
            this.fetchHtmlStrings();
        } else {
            this.noResults.style.display = 'block';
            this.keywords.clear();
            this.hideLoader();
        }
    }

    search(text, query, issues) {
        if (!issues) return false;

        // If query has too many words, use query else generate permutations
        // Generating permutations for large queries can negatively impact performance
        const queryWordsCount = query.trim().split(/\s+/).length;
        const permutations = queryWordsCount > 3 ? [query] : this.getPermutations(query);
    
        for (const permutation of permutations) {
            if (super.search(text, permutation)) {
                issues.forEach(issue => this.issues.add(issue));
                break;
            }
            
            if (this.keywords.size >= this.keywordsLimit) break;
            this.getKeywords(text, query);
        }
    }

    getPermutations(query) {
        return generateWordPermutations(query);
    }

    getKeywords(text, query) {
        text = text.trim().toLowerCase();
        query = query.trim().toLowerCase();

        const textWordsCount = query.split(' ').length;
        const textWords = text.split(/\s+/);

        for (let i = 0; i < textWords.length; i++) {
            if (this.keywords.size >= this.keywordsLimit) break;

            const phrase = textWords.slice(i, i + textWordsCount).join` `;
            const areaSimilar = areWordsSimilar(query, phrase, this.keywordsThreshold);

            if (areaSimilar && query !== phrase) this.keywords.add(phrase);
        }
    }

    renderKeywords() {
        const keywordsMarkup = [...this.keywords].map(keyword => `
            <li>
                <input type='button' value='${keyword}' />
            </li>
        `).join``;

        this.keywordsList.innerHTML = keywordsMarkup;

        this.keywordsListTitle.style.display = 'block';
        this.keywordsList.style.display = 'block';

        this.noResults.style.display = 'block';
    }
}

customElements.define('hoculus-search-by-chassis', SearchByChassis);
customElements.define('hoculus-search-by-text', SearchByText);